import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { getPredefinedDateRange } from '../../../../utils/date';
import { ICrossTestMetricSelection, IRange, IUser } from '../../../types';
import { authenticatedUserProvider } from '../../userProvider';
import { IDateRange } from '../dates/dateRangeProvider';
import { ISettingsTemplate } from './types';

export interface IBlockReportSettings {
  isMaxDatapoints: boolean;
  metrics: ICrossTestMetricSelection[];
  timeframe: IRange;
}

export interface IBlockReportSettingsState {
  selected: ISettingsTemplate<IBlockReportSettings>;
  templates: Array<ISettingsTemplate<IBlockReportSettings>>;
  timeframe: IDateRange;
  isMaxDatapoints: boolean;
}

interface IBlockReportSettingEntity extends IBlockReportSettingsState {
  selected: ISettingsTemplate<IBlockReportSettings>;
}

/**
 * @description observes dashboard settings
 */

export const blockReportSettingsProvider =
  (): Observable<IBlockReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap((user: IUser): Observable<IBlockReportSettingsState> => {
        return combineLatest([
          selectedTemplateProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          templatesProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          blockTimeframeProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
          maxDatapointsStatusProvider({
            userId: user.id,
            firestore: user.services.firestore,
          }),
        ]).pipe(
          map(
            (
              result: [
                ISettingsTemplate<IBlockReportSettings>,
                Array<ISettingsTemplate<IBlockReportSettings>>,
                IDateRange,
                boolean
              ]
            ): IBlockReportSettingsState => {
              const [selected, templates, timeframe, isMaxDatapoints] = result;
              if (selected?.id) {
                return {
                  selected,
                  templates,
                  timeframe,
                  isMaxDatapoints,
                };
              } else {
                return {
                  selected: null,
                  templates,
                  timeframe,
                  isMaxDatapoints,
                };
              }
            }
          )
        );
      })
    );
  };

interface ISelectedTemplateProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: ISelectedTemplateProviderArgs
): Observable<ISettingsTemplate<IBlockReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/blockReport`
    ) as DocumentReference<IBlockReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IBlockReportSettingEntity
      ): ISettingsTemplate<IBlockReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IBlockReportSettings>,
        curr: ISettingsTemplate<IBlockReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};

interface IBlockTimeframeArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the timeframe
 * @param userId
 * @returns
 */
const blockTimeframeProvider = (
  args: IBlockTimeframeArgs
): Observable<IDateRange> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/blockReport`
    ) as DocumentReference<IBlockReportSettingEntity>
  ).pipe(
    map((entity: IBlockReportSettingEntity): IDateRange => entity?.timeframe),
    distinctUntilChanged((prev: IDateRange, curr: IDateRange): boolean =>
      isEqual(prev, curr)
    )
  );
};

interface IDatapointArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the max/average datapoint selection
 * @param userId
 * @returns
 */
const maxDatapointsStatusProvider = (
  args: IDatapointArgs
): Observable<boolean> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/blockReport`
    ) as DocumentReference<IBlockReportSettingEntity>
  ).pipe(
    map(
      (entity: IBlockReportSettingEntity): boolean => entity?.isMaxDatapoints
    ),
    distinctUntilChanged((prev: boolean, curr: boolean): boolean =>
      isEqual(prev, curr)
    )
  );
};

interface ITemplateProviderArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: ITemplateProviderArgs
): Observable<Array<ISettingsTemplate<IBlockReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/blockReport/templates`
      ) as CollectionReference<ISettingsTemplate<IBlockReportSettings>>,
      where('active', '==', true)
    )
  );
};
